import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getExhibitorEnquiries, updateEnquiryStatus, deleteEnquiry } from '../../api/client';
import { toast } from 'sonner';
import { Mail, Phone, Building, MapPin, Trash2, RefreshCw } from 'lucide-react';

export const AdminEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEnquiries();
  }, [filter]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status_filter: filter } : {};
      const data = await getExhibitorEnquiries(params);
      setEnquiries(data.enquiries || []);
    } catch (error) {
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateEnquiryStatus(id, newStatus);
      toast.success('Status updated successfully');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    
    try {
      await deleteEnquiry(id);
      toast.success('Enquiry deleted successfully');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      converted: 'bg-green-100 text-green-700'
    };
    return colors[status] || colors.new;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Exhibitor Enquiries</h2>
            <p className="text-gray-600 mt-2">Manage exhibitor registration requests</p>
          </div>
          <Button onClick={fetchEnquiries} variant="outline" className="flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        <div className="flex gap-4 items-center">
          <label className="text-sm font-medium text-gray-700">Filter by status:</label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Enquiries</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Loading enquiries...</p>
            </div>
          </div>
        ) : enquiries.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Mail size={48} className="text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No enquiries found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {enquiries.map((enquiry) => (
              <Card key={enquiry._id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{enquiry.name}</CardTitle>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Building size={14} />
                          {enquiry.company}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {enquiry.country}
                        </span>
                      </div>
                    </div>
                    <Badge className={getStatusColor(enquiry.status)}>
                      {enquiry.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <a href={`mailto:${enquiry.email}`} className="text-sm text-green-600 hover:underline flex items-center gap-1">
                          <Mail size={14} />
                          {enquiry.email}
                        </a>
                      </div>
                      {enquiry.whatsapp && (
                        <div>
                          <p className="text-xs text-gray-500">WhatsApp</p>
                          <a href={`https://wa.me/${enquiry.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline flex items-center gap-1">
                            <Phone size={14} />
                            {enquiry.whatsapp}
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-500">Category</p>
                      <p className="text-sm font-medium">{enquiry.category}</p>
                    </div>

                    {enquiry.message && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Message</p>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
                          {enquiry.message}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">Status:</span>
                        <Select 
                          value={enquiry.status} 
                          onValueChange={(value) => handleStatusChange(enquiry._id, value)}
                        >
                          <SelectTrigger className="w-40 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(enquiry._id)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>

                    <p className="text-xs text-gray-400">
                      Submitted: {new Date(enquiry.created_at).toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
